import { IconButton } from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { type ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

export interface DrawerProps {
  /** Controla a visibilidade do drawer */
  open: boolean;
  /** Chamado ao fechar o drawer (botão X, overlay ou Escape) */
  onClose: () => void;
  /** Título exibido no header */
  title: ReactNode;
  /** Texto muted exibido acima do título (ex: data/hora) */
  subtitle?: ReactNode;
  /** Slot para o rodapé do drawer */
  footer?: ReactNode;
  /** Conteúdo do corpo do drawer */
  children: ReactNode;
}

/**
 * Painel lateral genérico renderizado via portal sobre toda a página.
 *
 * Anima entrada (slide + fade) e saída antes de desmontar.
 * Fecha ao clicar no overlay ou pressionar Escape.
 * Bloqueia scroll do body enquanto aberto.
 *
 * @param open - Visibilidade do drawer
 * @param onClose - Callback de fechamento
 * @param title - Título do header
 * @param subtitle - Texto muted acima do título
 * @param footer - Conteúdo do rodapé
 * @param children - Corpo do drawer
 */
export function Drawer({ open, onClose, title, subtitle, footer, children }: DrawerProps) {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setClosing(false);
      setMounted(true);
    } else if (mounted) {
      setClosing(true);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!mounted) return null;

  return createPortal(
    <div className="fixed inset-none z-50 flex">
      {/* Overlay */}
      <div
        className={[
          'absolute inset-none bg-base-neutral-black/medium',
          closing ? 'drawer-overlay-out' : 'drawer-overlay-in',
        ].join(' ')}
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={[
          'relative ml-auto flex h-full w-full max-w-[800px] flex-col bg-ctx-layout-surface shadow-level3',
          closing ? 'drawer-panel-out' : 'drawer-panel-in',
        ].join(' ')}
        onAnimationEnd={() => {
          if (closing) {
            setMounted(false);
            setClosing(false);
          }
        }}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between border-b-hairline border-ctx-layout-border px-xxs py-mini">
          <div>
            {subtitle && (
              <p className="ts-subtitle-xxxs text-ctx-content-base mb-quark">{subtitle}</p>
            )}
            <h2 className="ts-heading-sm text-ctx-content-title m-0">{title}</h2>
          </div>
          <IconButton
            size="sm"
            onClick={onClose}
            ariaLabel={t('drawer.close')}
            hierarchy="tertiary"
            icon={XMarkIcon}
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-xxs">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="flex shrink-0 items-center justify-between border-t-hairline border-ctx-layout-border px-sm py-mini">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
